import { SiteMap } from './types';
import { defineChain, Chain, getAddress } from 'viem';
import { http } from '@wagmi/core';
import { createConfig } from '@privy-io/wagmi';

// Environment variables interface
export interface EnvironmentConfig {
  NEXT_PUBLIC_SITE_URL: string;
  NEXT_PUBLIC_GM_API_SERVER: string;
  NEXT_PUBLIC_PRIVY_APP_ID: string;
  NEXT_PUBLIC_ENGINE_GTAG?: string;
  NEXT_PUBLIC_SENTRY_DSN?: string;
  NEXT_PUBLIC_DEFAULT_CHAIN_NAME?: string;
  NEXT_PUBLIC_LOCAL_CHAIN_ID?: string;
  NEXT_PUBLIC_ARBSEPOLIA_RPC: string;
  NEXT_PUBLIC_LOCAL_CHAIN_RPC_TARGET?: string;
  NEXT_PUBLIC_DEFAULT_MAO_DIST_NAME: string;
  NEXT_PUBLIC_GAME_MASTER_ADDRESS: string;
  NEXT_PUBLIC_IPFS_GATEWAY: string;
  NEXT_PUBLIC_DEMO_MODE: string;
  NEXT_PUBLIC_GITCOIN_CAMPAIGN_URL?: string;
  NEXT_PUBLIC_ENVIO_GRAPHQL_ENDPOINT: string;
  NEXT_PUBLIC_ENVIO_API_KEY?: string;
  NEXT_PUBLIC_DISCORD_BOT_INVITE_LINK: string;
  NEXT_PUBLIC_REQUEST_FELLOWSHIP_FORM_URL: string;
  NEXT_PUBLIC_TELEGRAM_BOT_USERNAME: string;
}

interface EnvConfig {
  key: string;
  devFallback?: string;
}

export function getBatchProcessEnv<T>(configs: EnvConfig[]): T {
  let ret = {} as T;
  let missing: string[] = [];

  for (let config of configs) {
    const value = process.env[config.key];
    if (!value) {
      if (config.devFallback && process.env.NODE_ENV !== 'production') {
        (ret as any)[config.key] = config.devFallback;
      } else {
        missing.push(config.key);
      }
    } else {
      (ret as any)[config.key] = value;
    }
  }

  if (missing.length > 0) {
    throw new Error('Missing environment variables: \n' + missing.join(', \n'));
  }

  return ret;
}

export function getProcessEnv(key: string, devFallback?: string) {
  const ret = process.env[key];
  if (!ret) {
    if (devFallback && process.env.NODE_ENV !== 'production') {
      return devFallback;
    }
    throw new Error(key + ' must be exported in env');
  }
  return ret;
}

// Get all environment variables at once with fallbacks
const envConfigs: EnvConfig[] = [
  { key: 'NEXT_PUBLIC_SITE_URL' },
  { key: 'NEXT_PUBLIC_GM_API_SERVER' },
  { key: 'NEXT_PUBLIC_PRIVY_APP_ID' },
  { key: 'NEXT_PUBLIC_ENGINE_GTAG', devFallback: 'XXX' },
  { key: 'NEXT_PUBLIC_SENTRY_DSN', devFallback: 'XXX' },
  { key: 'NEXT_PUBLIC_DEFAULT_CHAIN_NAME', devFallback: 'arbsepolia' },
  { key: 'NEXT_PUBLIC_LOCAL_CHAIN_ID' },
  { key: 'NEXT_PUBLIC_ARBSEPOLIA_RPC' },
  { key: 'NEXT_PUBLIC_DEFAULT_MAO_DIST_NAME' },
  { key: 'NEXT_PUBLIC_GAME_MASTER_ADDRESS' },
  { key: 'NEXT_PUBLIC_IPFS_GATEWAY' },
  { key: 'NEXT_PUBLIC_DEMO_MODE' },
  { key: 'NEXT_PUBLIC_ENVIO_GRAPHQL_ENDPOINT' },
  { key: 'NEXT_PUBLIC_DISCORD_BOT_INVITE_LINK' },
  { key: 'NEXT_PUBLIC_REQUEST_FELLOWSHIP_FORM_URL' },
  { key: 'NEXT_PUBLIC_TELEGRAM_BOT_USERNAME', devFallback: 'rankify_bot' },
];

const envs = getBatchProcessEnv<Record<string, string>>(envConfigs);

export const NODE_ENV = process.env.NODE_ENV;
export const BASE_URL = envs.NEXT_PUBLIC_SITE_URL;
export const GM_API_SERVER = envs.NEXT_PUBLIC_GM_API_SERVER;
export const PRIVY_APP_ID = envs.NEXT_PUBLIC_PRIVY_APP_ID;
export const GTAG = envs.NEXT_PUBLIC_ENGINE_GTAG;
export const SENTRY_DSN = envs.NEXT_PUBLIC_SENTRY_DSN;
export const DEFAULT_CHAIN_NAME = envs.NEXT_PUBLIC_DEFAULT_CHAIN_NAME;

const LOCALHOST_CHAIN_ID = envs.NEXT_PUBLIC_LOCAL_CHAIN_ID;
const ARBSEPOLIA_RPC = envs.NEXT_PUBLIC_ARBSEPOLIA_RPC;

export const SUPPORTED_CHAINS: [Chain, ...Chain[]] = [] as unknown as [Chain, ...Chain[]];

DEFAULT_CHAIN_NAME === 'localhost' &&
  SUPPORTED_CHAINS.push(
    defineChain({
      id: parseInt(LOCALHOST_CHAIN_ID),
      name: 'localhost',
      nativeCurrency: {
        decimals: 18,
        name: 'Ethereum',
        symbol: 'ETH',
      },
      rpcUrls: {
        default: {
          http: [process.env.NEXT_PUBLIC_LOCAL_CHAIN_RPC_TARGET ?? ''],
        },
        privyWalletOverride: {
          http: [process.env.NEXT_PUBLIC_LOCAL_CHAIN_RPC_TARGET ?? ''],
        },
      },
    })
  );

SUPPORTED_CHAINS.push(
  defineChain({
    id: parseInt('0x17b59'),
    name: 'anvil',
    nativeCurrency: {
      decimals: 18,
      name: 'Ethereum',
      symbol: 'ETH',
    },
    rpcUrls: {
      default: {
        http: ['https://devnet.rankify.it'],
      },
      privyWalletOverride: {
        http: ['https://devnet.rankify.it'],
      },
    },
  }),
  defineChain({
    id: parseInt('0x66eee'),
    name: 'arbsepolia',
    nativeCurrency: {
      decimals: 18,
      name: 'Ethereum',
      symbol: 'ETH',
    },
    rpcUrls: {
      default: {
        http: [ARBSEPOLIA_RPC],
      },
      privyWalletOverride: {
        http: [ARBSEPOLIA_RPC],
      },
    },
    blockExplorers: {
      default: {
        name: 'Arbiscan Explorer',
        url: 'https://sepolia.arbiscan.io/',
      },
    },
  })
);

export const wagmiConfig = createConfig({
  chains: SUPPORTED_CHAINS,
  transports: { ...Object.fromEntries(SUPPORTED_CHAINS.map((chain) => [chain.id, http()])) },
});

const _DEFAULT_CHAIN: Chain | undefined = SUPPORTED_CHAINS.find((chain) => chain.name === DEFAULT_CHAIN_NAME);
if (!_DEFAULT_CHAIN) throw new Error('Default chain not found');
export const DEFAULT_CHAIN: Chain = _DEFAULT_CHAIN;

console.log('DEFAULT_CHAIN', DEFAULT_CHAIN);

export const SITEMAP: SiteMap = [
  {
    title: 'Challenges',
    path: '/challenges',
    type: 'EMPTY',
  },
  {
    title: 'Research',
    path: '/research',
    type: 'CONTENT',
    children: [
      {
        title: 'Concepts',
        path: '/research/concepts',
        type: 'CONTENT',
      },
      {
        title: 'Mechanics',
        path: '/research/mechanics',
        type: 'CONTENT',
      },
      {
        title: 'Deep dive',
        path: '/research/paper',
        type: 'CONTENT',
      },
    ],
  },
  {
    title: 'Development',
    path: '/development',
    type: 'CONTENT',
    children: [
      {
        title: 'Docs',
        path: '/docs',
        type: 'CONTENT',
      },
      {
        title: 'GitHub',
        path: 'https://github.com/rankify-it',
        type: 'EXTERNAL',
      },
      // {
      //     title: "Roadmap",
      //     path: "/development/roadmap",
      //     type: "CONTENT",
      // },
    ],
  },
  {
    title: 'About',
    path: '/about',
    type: 'CONTENT',
    children: [
      {
        title: 'Our story',
        path: '/about/story',
        type: 'CONTENT',
      },
      {
        title: 'Contact',
        path: '/about/contact',
        type: 'CONTENT',
      },
      {
        title: 'Contribute',
        path: '/about/contribute',
        type: 'CONTENT',
      },
    ],
  },
  {
    type: 'FOOTER_CATEGORY',
    title: 'Terms',
    path: '/terms',
  },
];

export const defaultMetaTags = {
  title: 'Rankify',
  keywords:
    'Fellowship, Threads, Round Robin Disscussion, Quadratic Voting, Blockchain; Web3, ZeroKnowledge proofs, Governance, DAO, Discord',
  description: 'Hosting Fellowship Threads, enabling consensus building, and sparking community engagement',
  url: BASE_URL,
  image: '/logo_preview.png',
};

export const DEFAULT_MAO_DIST_NAME = envs.NEXT_PUBLIC_DEFAULT_MAO_DIST_NAME;

export const GAME_MASTER_ADDRESS = getAddress(envs.NEXT_PUBLIC_GAME_MASTER_ADDRESS);

export const IPFS_GATEWAY = envs.NEXT_PUBLIC_IPFS_GATEWAY;

export const DEMO_MODE: boolean = envs.NEXT_PUBLIC_DEMO_MODE === 'true';

export const GITCOIN_CAMPAIGN_URL = process.env.NEXT_PUBLIC_GITCOIN_CAMPAIGN_URL;

export const ENVIO_GRAPHQL_ENDPOINT = envs.NEXT_PUBLIC_ENVIO_GRAPHQL_ENDPOINT;

export const ENVIO_API_KEY = process.env.NEXT_PUBLIC_ENVIO_API_KEY;

export const DISCORD_BOT_INVITE_LINK = envs.NEXT_PUBLIC_DISCORD_BOT_INVITE_LINK;

export const REQUEST_FELLOWSHIP_FORM_URL = envs.NEXT_PUBLIC_REQUEST_FELLOWSHIP_FORM_URL;

export const TELEGRAM_BOT_USERNAME = envs.NEXT_PUBLIC_TELEGRAM_BOT_USERNAME;

export const JWT_SECRET = envs.NEXT_PUBLIC_JWT_SECRET || 'rankify-jwt-secret';
